import {
    Groups as FamilyIcon,
    Person as PersonIcon,
    PhotoLibrary as PhotoIcon,
    CloudUpload as UploadIcon,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Paper,
    Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useCookies } from "react-cookie";
import { useLocation } from 'react-router-dom';
import { JWT } from '../Auth';
import BuildInfo from '../components/BuildInfo';
import PWAInstall from '../components/PWAInstall';

export function doAwsLogin(): void {
    document.location =
        'https://family-archive.auth.ca-central-1.amazoncognito.com/oauth2/authorize?client_id=5duh1tun4ndvgsm5lbnrhcebl4&response_type=token&scope=aws.cognito.signin.user.admin+openid+profile&redirect_uri=https%3A%2F%2Fdirksvideo.link%2F';
}

const Home: React.FC = () => {
    const [cookies, setCookie] = useCookies(['cognito']);
    const { hash } = useLocation();
    const jwt = new JWT(cookies['cognito']);

    if (jwt && !jwt.isValid()) {
        document.cookie = "cognito=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }

    useEffect(() => {
        if (hash) {
            const parts: string[] = hash.split('&');

            parts.forEach(part => {
                const token_start = part.indexOf("id_token=");
                if (token_start !== -1) {
                    const tokenBody = part.substring(token_start + "id_token=".length);
                    setCookie('cognito', tokenBody);
                }
            })

            document.location = '/files';
        }
    }, [hash, setCookie]);

    function doLocalLogin(): void {
        document.location = 'https://family-archive.auth.ca-central-1.amazoncognito.com/oauth2/authorize?client_id=6dvug1qs413v1c8d96f2i77nl0&response_type=token&scope=aws.cognito.signin.user.admin+email+openid+phone+profile&redirect_uri=http%3A%2F%2Flocalhost%3A3000';
    }

    const isLoggedIn = jwt.isValid();
    const userName = isLoggedIn ? jwt.getName() : null;

    const HeroSection = () => (
        <Paper
            sx={{
                background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
                color: 'white',
                p: 6,
                mb: 4,
                borderRadius: 3,
                textAlign: 'center',
            }}
        >
            <FamilyIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
            <Typography variant="h2" component="h1" gutterBottom>
                Welcome to Family Archive
            </Typography>
            <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
                Preserving your precious memories for generations to come
            </Typography>
            {isLoggedIn ? (
                <Box>
                    <Chip
                        icon={<PersonIcon />}
                        label={`Welcome back, ${userName}`}
                        color="secondary"
                        size="medium"
                        sx={{ fontSize: '1.1rem', py: 3 }}
                    />
                </Box>
            ) : (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                        Please log in to access your family archive
                    </Typography>
                    {document.location.hostname === 'localhost' ? (
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={doLocalLogin}
                            sx={{ mr: 2 }}
                        >
                            Login (Local)
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={doAwsLogin}
                        >
                            Login
                        </Button>
                    )}
                </Box>
            )}
        </Paper>
    );

    const QuickActions = () => (
        <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => window.location.href = '/files'}>
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                            <PhotoIcon sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="h5" gutterBottom>
                            Browse Files
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            View and manage your family photos, videos, and documents
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => window.location.href = '/upload'}>
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                            <UploadIcon sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="h5" gutterBottom>
                            Upload Memories
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Add new photos, videos, and documents to your family archive
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const Features = () => (
        <Box sx={{ mt: 6 }}>
            <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
                Features
            </Typography>
            <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            🔒 Secure Storage
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Your memories are safely stored with enterprise-grade security
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            📱 Easy Access
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Access your family archive from any device, anywhere
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            🎥 Video Support
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Upload and stream family videos with automatic transcoding
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );

    return (
        <Box>
            <PWAInstall />
            <HeroSection />
            {isLoggedIn && <QuickActions />}
            <Features />
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <BuildInfo />
            </Box>
        </Box>
    );
}

export default Home;
